import { Directive, Input, OnChanges, OnInit, Output, signal, SimpleChanges, WritableSignal } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";

export interface IEditable { 
  editableFields$: Observable<Set<string>>;     // a list of properties that are editable for the object by the current user 
  validate(): void;                             // validates the editable object, changing only the errors/warnings collections 
  clean(): void;                                // cleans the editable object data by changing its properties if necessary based on current state 
  errors$: Observable<any>;                     // a list of input errors, keyed by field name 
  warnings$: Observable<any>;                   // a list of input warnings, keyed by field name 
  IsDirty: boolean;                             // whether or not the state of the object is different from how it was originally loaded, PascalCase because it's a business object (C#) property
  isEditable$: Observable<boolean>;             // whether or not this field is editable (could be read-only)
  onChange(): void;                             // call this whever a change has been conitted by the user. It usually triggers validate/clean/dirty, etc, in the business object 
  touched: boolean;                             // whether or not the state of the object is different from how it was originally loaded, PascalCase because it's a business object (C#) property
  // non-observable properties (for backward compatability)  
  errors: any;
  warnings: any;
  editableFields: Set<string>;
  // end of non-observable (backward compatability)
  toJSON(): {};                                 // This function is called whenever we stringify a js object and is defined below
  errorsSig: WritableSignal<any>;               // As we move to using signals this will eventually replace errors$
  warningsSig: WritableSignal<any>;             // As we move to using signals this will eventually replace warnings$
  syncAlerts(): void;                           // used to update the reactive alerts (errors/warnings) after object validation completes.  Currently updates observables and signals.
}

// use obj[prop] as the model -- this is what the widget should be bound to at the end of the day. 
@Directive()
export class EditableFieldBase implements OnInit, OnChanges {
  // Note: TypeScript interfaces are compile-time only.
  // Angular will not automatically map or convert incoming values to IEditable.
  // If the parent passes `any`, runtime validation/conversion does not occur.
  // The passed object must already match the IEditable shape.
  @Input({ required: true }) public obj: IEditable;      // the transformed representation of the business object.
  @Input({ required: true }) public prop: string;        // the name of the property in the obj that will be edited -- the ngModel
  @Input() public id: string;                            // the unique identifier for the input.  If only a single instance of this input is on the page we can just re-use prop for the id.
  @Input({ required: true }) public label: string;       // the label to show before the field.  A screen reader label must be provided even if hidden for ADA compliance.
  @Input() public labelCols: number;                     // the number of columns the lable should span 
  @Input() public helpText: string;                      // optional help text associated with the widget
  @Input() public allowReadOnlyWarnings: boolean = false //  an override to allow warnings to be seen when fields are in read only form

  //  Optional labelCol sizes for grid flexibility
  @Input() public labelColsSm: number = null;
  @Input() public labelColsMd: number = null;
  @Input() public labelColsLg: number = null;
  @Input() public labelColsXl: number = null;
  @Input() public labelColsXxl: number = null;

  // TODO:  See if the implementeations of this are still needed now that we have syncAlerts() - May 2026 CS
  // triggerValidate is used to tell the field to run validation again. this could be because the obj[prop] value has been changed
  @Input() triggerValidate: boolean;


  // value$ is a behavior subject so it can be subscribed to within the derived classes and still report a value
  @Output() public value$: BehaviorSubject<any> = new BehaviorSubject(null);   // the current value reported by the widget 
  
  
  public isEditable$: Observable<boolean>;
  public error$: Observable<string>;
  public warning$: Observable<string>;

  constructor() {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['obj']) {
      // the object has changed so check its validity and editability     
      this.validate();
      this.editable();
      // Used whenever we stringify an object, for example when we put/post an editable object to the api
      // This allows us to exclude the observable properties from a json string
      // We get circular errors if we try to stringify observable properties    
      this.obj.toJSON = function () {
        var result = {};
        for (var x in this) {
          if (x !== "errors$" && x !== "warnings$" && x !== "editableFields$") {
            result[x] = this[x];
          }
        }
        return result;
      }
    }

    // If this input is changed we call validate again - this is a quick fix to get validate to work when the
    // bound object value is changed in ts. This isn't the best solution, we should revisit these fields with this
    // use case in mind and find out how we can get this to work without an extra input. (PP 09/2024)
    if (!!changes.triggerValidate)
      this.validate();

    //  This helps re-sync the validation on the object if it happens to be missing syncAlerts.  This typically
    //  happens after the object gets sent to the API and a new object replaces it.
    if (!!changes.obj?.currentValue && !changes.obj.currentValue?.syncAlerts) {
      const curr: any = changes.obj.currentValue;
      curr.syncAlerts = () => this.syncAlerts(curr);
    }
  }

  public ngOnInit() {
    if (!this.id)
      this.id = this.prop;

    this.setLabelSizes();


    // Angular will not automatically map or convert incoming values to IEditable.
    // we have to check the object to see if it has the function, otherwise add it
    if (!this.obj.syncAlerts) 
      this.obj.syncAlerts = () => this.syncAlerts(this.obj);

    this.validate();

    this.value$.next(this.obj[this.prop]);
  }
  
  //  Make sure editableFields$ is wrapped in a BehaviorSubject when added to your object.  If you forget to wrap it this
  //  editable() call will bomb silently in the background and the rest of ngOnChanges will not continue.
  protected editable(): void {
    if (!this.obj.editableFields$) {
      //  If there are no editable fields (eg: filters) or the list of editable fields contains this field, enable editing.
      var isEditable = !this.obj.editableFields || this.obj.editableFields.has(this.prop);
      this.isEditable$ = new BehaviorSubject<boolean>(false);
      (this.isEditable$ as BehaviorSubject<boolean>).next(isEditable);
    } else {
      this.isEditable$ = this.obj.editableFields$.pipe(
        //tap((val) => console.log('╔═ isEditable$', val)),        
        map(val => val.has(this.prop)),
        //tap(val => console.log('╚═ isEditable$', val)),        
      )
    }   
  }

  //  Responsible for keeping our reactive alerts updated when validation is complete.
  //  Ideally triggered at the end of the validation on the object.
  protected syncAlerts(obj: any) {
    this.addAlerts(obj);

    obj.errors$.next(obj.errors);
    obj.warnings$.next(obj.warnings);
       
    this.updateSignal(obj.errorsSig, obj.errors);
    this.updateSignal(obj.warningsSig, obj.warnings);
  }

  private addAlerts(obj: any) {
    if (!obj.errors$)
      obj.errors$ = new BehaviorSubject({});

    if (!obj.warnings$)
      obj.warnings$ = new BehaviorSubject({});

    if (!obj.errorsSig)
      obj.errorsSig = signal<any>({});

    if (!obj.warningsSig)
      obj.warningsSig = signal<any>({});
  }

  protected validate(): void {
    this.addAlerts(this.obj);
    this.obj.validate();
    // TODO: Once all of our apps are using errors$ and warnings$ we could remove the code below and change the html in our editable components
    // to just directly async on errors$ and warnings$ instead of doing the map below.
    
    if (!this.obj.errors$) {      
      // report any validation errors
      let field = Object.keys(this.obj.errors).find(f => f == this.prop);
      this.error$ = new BehaviorSubject<string>("");
      (this.error$ as BehaviorSubject<string>).next(this.obj.errors[field] ?? "");
    } else {      
      this.error$ = this.obj.errors$.pipe(
         //tap((val) => console.log('╔═ error$', val)),            
         map(val => val[this.prop]),
         //tap(val => console.log('╚═ error$', val)),   
       )
    }

    if (!this.obj.warnings$) {
      // report any validation warning
      let field = Object.keys(this.obj.warnings).find(f => f == this.prop);
      this.warning$ = new BehaviorSubject<string>("");
      (this.warning$ as BehaviorSubject<string>).next(this.obj.warnings[field] ?? "");
    } else {
      this.warning$ = this.obj.warnings$.pipe(
        //tap((val) => console.log('╔═ warning$', val)),   
        map(val => val[this.prop]),
        //tap(val => console.log('╚═ warning$', val)),  
      )
    }
  }

  //  TODO:  Not sure this is the way we want it yet but I need to get this code committed to get
  //  a fix up.  Will come back to this (hopefully) soon.
  //  Triggers updates to the errors and warnings signals only when there are changes.  Intended
  //  to cut down on the chattiness so we can watch a signal that updates only when there are actual
  //  changes.
  private updateSignal(sig: WritableSignal<any>, items: any) {
    const sigItems = sig();
    const sigKeys = Object?.keys(sigItems);
    const itemKeys = Object?.keys(items ?? {});

    if (sigKeys.length != itemKeys.length) {
      sig.set(items);
      return;
    }

    for (const key of sigKeys) {
      if (sigItems[key] != items[key]) {
        sig.set(items);
        return;
      }
    }
  }

  // Call this when the user has committed to a value change. This could be anything from a single
  // checkbox click or settinging several form values and clicking "ok" or things like that. 
  protected onChange() {
    this.setLabelSizes();

    this.obj.onChange();
    this.value$.next(this.obj[this.prop]);    
  }

  private setLabelSizes() {
    //  Sizes the optional label cols if they get changed to a non-number or are not set
    if (Number.isNaN(this.labelColsSm) || (this.labelColsSm == null))
      this.labelColsSm = this.labelCols;

    if (Number.isNaN(this.labelColsMd) || (this.labelColsMd == null))
      this.labelColsMd = this.labelColsSm;

    if (Number.isNaN(this.labelColsLg) || (this.labelColsLg == null))
      this.labelColsLg = this.labelColsMd;

    if (Number.isNaN(this.labelColsXl) || (this.labelColsXl == null))
      this.labelColsXl = this.labelColsLg;

    if (Number.isNaN(this.labelColsXxl) || (this.labelColsXxl == null))
      this.labelColsXxl = this.labelColsXl;
  }

  protected onBlur() {
    //console.log("touched");
    this.obj.touched = true;
  }
}
